/**
 * The one downhole unit formatter. Every measured value on a /downhole screen
 * goes through here.
 *
 * WHAT IS STORED. Every dh_* measurement is IMPERIAL — in, ft, lbs, ft·lbs, °F,
 * klbs, psi — and the API returns it as stored, with the tenant's `unit_system`
 * beside it in `meta` (downhole.routes.ts: "Nothing here converts"). The
 * product is sold in Canada and the US, so the screen has to do the
 * converting: an imperial tenant reads the stored figure, a metric tenant reads
 * it in m, mm, kg, N·m, °C, kN, kPa, m/h, kg/m³ and L/min.
 *
 * WHICH WAY IT GOES. Display converts imperial → the tenant's system.
 * `toStoredImperial` converts an INPUT back the other way, so a metric reader
 * can type 4,000 N·m and the API still receives ft·lbs. **Never send metric to
 * the API** — the column has no unit beside it, and a metric figure stored
 * there would read back as imperial and be wrong by a constant factor that
 * looks like a plausible number.
 *
 * A NULL unit system shows the value AS STORED, labelled imperial. That is not
 * assuming the tenant is American; it is printing the figure with the unit it
 * actually has. Converting to metric on a guess would be the wrong-number-
 * that-looks-right this repo keeps finding.
 *
 * ABSENT IS `—`. A null, an empty string or anything non-finite renders the em
 * dash with no unit after it — never `0 m`, never `null"`.
 *
 * This is formatting, not recomputation: the API returns one figure and this
 * prints that figure in another unit. It does not derive a figure the API
 * also returns (CLAUDE.md non-negotiable 5).
 */
import type { DhUnitSystem } from '../types/downhole';
import { fmtQty } from './display-labels';

export type DhQuantity =
  /** in ↔ mm — od, id_inner, fish_neck_od, hole_size, od_measurement */
  | 'diameter'
  /** ft ↔ m — length_ft, depths, footage */
  | 'length'
  /** lbs ↔ kg — weight_lbs */
  | 'weight'
  /** ft·lbs ↔ N·m — makeup_torque, torque_avg_ftlbs */
  | 'torque'
  /** °F ↔ °C — max_temp_f */
  | 'temperature'
  /** klbs ↔ kN — max_wob_klbs, wob_avg_klbs */
  | 'force'
  /** psi ↔ kPa — pressure_rating (INTEGER, psi by the dh_* imperial convention) */
  | 'pressure'
  /** ft/hr ↔ m/hr — rop_avg */
  | 'rop'
  /** ppg (lb/US gal) ↔ kg/m³ — mud_weight */
  | 'density'
  /** US gal/min ↔ L/min — flow_rate_gpm */
  | 'flow';

interface UnitSpec {
  imperial: string;
  metric: string;
  /** Default decimals for each system. */
  imperialDigits: number;
  metricDigits: number;
  toMetric: (v: number) => number;
  toImperial: (v: number) => number;
}

/* Exact definitions (NIST SP 811), not rounded factors, so a value typed in
 * metric and stored round-trips to the figure the reader typed. */
const IN_MM = 25.4;
const FT_M = 0.3048;
const LB_KG = 0.45359237;
const FTLB_NM = 1.3558179483314004;
const KLB_KN = 4.4482216152605;
const PSI_KPA = 6.894757293168361;
/** 1 lb/US gal = 0.45359237 kg / 0.003785411784 m³. */
const PPG_KGM3 = 119.82642731689663;
const GAL_L = 3.785411784;

const UNITS: Record<DhQuantity, UnitSpec> = {
  diameter: {
    imperial: 'in', metric: 'mm', imperialDigits: 3, metricDigits: 1,
    toMetric: (v) => v * IN_MM, toImperial: (v) => v / IN_MM,
  },
  length: {
    imperial: 'ft', metric: 'm', imperialDigits: 1, metricDigits: 2,
    toMetric: (v) => v * FT_M, toImperial: (v) => v / FT_M,
  },
  weight: {
    imperial: 'lb', metric: 'kg', imperialDigits: 0, metricDigits: 0,
    toMetric: (v) => v * LB_KG, toImperial: (v) => v / LB_KG,
  },
  torque: {
    imperial: 'ft·lb', metric: 'N·m', imperialDigits: 0, metricDigits: 0,
    toMetric: (v) => v * FTLB_NM, toImperial: (v) => v / FTLB_NM,
  },
  temperature: {
    imperial: '°F', metric: '°C', imperialDigits: 0, metricDigits: 0,
    toMetric: (v) => ((v - 32) * 5) / 9, toImperial: (v) => (v * 9) / 5 + 32,
  },
  force: {
    imperial: 'klb', metric: 'kN', imperialDigits: 1, metricDigits: 1,
    toMetric: (v) => v * KLB_KN, toImperial: (v) => v / KLB_KN,
  },
  pressure: {
    imperial: 'psi', metric: 'kPa', imperialDigits: 0, metricDigits: 0,
    toMetric: (v) => v * PSI_KPA, toImperial: (v) => v / PSI_KPA,
  },
  rop: {
    imperial: 'ft/h', metric: 'm/h', imperialDigits: 1, metricDigits: 1,
    toMetric: (v) => v * FT_M, toImperial: (v) => v / FT_M,
  },
  density: {
    imperial: 'ppg', metric: 'kg/m³', imperialDigits: 1, metricDigits: 0,
    toMetric: (v) => v * PPG_KGM3, toImperial: (v) => v / PPG_KGM3,
  },
  flow: {
    imperial: 'gpm', metric: 'L/min', imperialDigits: 0, metricDigits: 0,
    toMetric: (v) => v * GAL_L, toImperial: (v) => v / GAL_L,
  },
};

const isMetric = (system: DhUnitSystem | null | undefined) => system === 'metric';

/** A finite number, or null. `''`, null, undefined and NaN are all absent. */
function finite(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The unit label the tenant reads for a quantity: 'mm' or 'in', 'm' or 'ft'. */
export function dhUnitLabel(quantity: DhQuantity, system: DhUnitSystem | null | undefined): string {
  const spec = UNITS[quantity];
  return isMetric(system) ? spec.metric : spec.imperial;
}

/**
 * A stored (imperial) value in the tenant's system, as a number. Null when
 * absent. For an input's initial value and for sorting; screens print with
 * `formatDh`.
 */
export function toDisplayValue(
  stored: unknown,
  quantity: DhQuantity,
  system: DhUnitSystem | null | undefined,
): number | null {
  const n = finite(stored);
  if (n === null) return null;
  return isMetric(system) ? UNITS[quantity].toMetric(n) : n;
}

/**
 * A stored value as the text an EDIT input starts with, in the tenant's
 * system: plain digits, no thousands separator, no unit. Imperial is the
 * stored figure as written; metric is rounded to the system's display digits,
 * so a 6.5 in OD opens as `165.1`, not `165.10000000000002`. Absent → `''`.
 *
 * A metric value opened and saved unchanged is NOT a round trip — 9,850 ft
 * opens as 3002.28 m and would go back as 9,849.99 ft. A form that starts from
 * this sends only the fields whose text the reader actually changed.
 */
export function toInputValue(stored: unknown, quantity: DhQuantity, system: DhUnitSystem | null | undefined): string {
  const n = finite(stored);
  if (n === null) return '';
  if (!isMetric(system)) return String(n);
  const f = 10 ** UNITS[quantity].metricDigits;
  return String(Math.round(UNITS[quantity].toMetric(n) * f) / f);
}

/**
 * The scale of the dh_* column each quantity is stored in, so a converted input
 * is rounded to what the column keeps rather than to what it happens to be.
 *
 *   diameter     DECIMAL(6,3) — od, id_inner, fish_neck_od, hole_size. At 2 a
 *                metric 165.1 mm stored 6.5 in, but 168.3 mm stored 6.63 where
 *                the column keeps 6.626.
 *   temperature  INTEGER — max_temp_f. The API refuses a fraction (400), and
 *                most whole °C are not whole °F.
 *   pressure     INTEGER — pressure_rating. Same.
 *   everything else is DECIMAL(n,2) or wider: 2.
 */
const STORED_DIGITS: Partial<Record<DhQuantity, number>> = { diameter: 3, temperature: 0, pressure: 0 };

/**
 * A value the reader typed, in THEIR system, converted to the imperial figure
 * the API stores. Null when the input is blank or not a number — a blank field
 * is "not supplied", never 0.
 *
 * Rounded to `digits` — by default the stored column's scale (STORED_DIGITS
 * above, else 2) — so a converted value does not arrive with fifteen decimals.
 */
export function toStoredImperial(
  input: unknown,
  quantity: DhQuantity,
  system: DhUnitSystem | null | undefined,
  digits = STORED_DIGITS[quantity] ?? 2,
): number | null {
  const n = finite(typeof input === 'string' ? input.trim() : input);
  if (n === null) return null;
  const imperial = isMetric(system) ? UNITS[quantity].toImperial(n) : n;
  const f = 10 ** digits;
  return Math.round(imperial * f) / f;
}

/**
 * Print a stored value in the tenant's system with its unit: `6.5 in` /
 * `165.1 mm`, `9,850.0 ft` / `3,002.28 m`. Absent → `—`.
 *
 * `digits` overrides the default decimals for the tenant's system. Imperial
 * diameters drop trailing zeros (6.500 → 6.5), because a stored 6.5 printed as
 * 6.500 claims a precision nobody measured.
 */
export function formatDh(
  stored: unknown,
  quantity: DhQuantity,
  system: DhUnitSystem | null | undefined,
  options?: { digits?: number; minDigits?: number; unit?: boolean },
): string {
  const value = toDisplayValue(stored, quantity, system);
  if (value === null) return '—';
  const spec = UNITS[quantity];
  const metric = isMetric(system);
  const digits = options?.digits ?? (metric ? spec.metricDigits : spec.imperialDigits);
  const trimZeros = !metric && quantity === 'diameter' && options?.digits === undefined;
  const text = value.toLocaleString('en-US', {
    minimumFractionDigits: trimZeros ? 0 : Math.min(options?.minDigits ?? digits, digits),
    maximumFractionDigits: digits,
  });
  // -0 is not a measurement.
  const clean = text === '-0' || /^-0\.0+$/.test(text) ? text.slice(1) : text;
  return options?.unit === false ? clean : `${clean} ${metric ? spec.metric : spec.imperial}`;
}

/**
 * A wear percent as the API returned it (USED). Null → `—`, never 0%.
 * Hours and counts carry no unit system: print them with fmtHours / fmtQty.
 */
export function formatWearPercent(value: number | string | null | undefined): string {
  const text = fmtQty(value);
  return text === '—' ? text : `${text}%`;
}

/**
 * A footage RATE — money per unit of length — in the tenant's system.
 * `dh_rates.footage_rate` is stored per FOOT (api#906, as every dh_* length is
 * feet). A metric tenant reads and types it per METRE: $/m = $/ft ÷ 0.3048,
 * and back, $/ft = $/m × 0.3048, rounded to the column's 4 places. It is the
 * inverse of the length conversion because the unit is in the denominator.
 */
export function footageRateUnit(system: DhUnitSystem | null | undefined): string {
  return isMetric(system) ? 'm' : 'ft';
}

export function toDisplayFootageRate(stored: unknown, system: DhUnitSystem | null | undefined): number | null {
  const n = finite(stored);
  if (n === null) return null;
  return isMetric(system) ? n / FT_M : n;
}

export function toStoredFootageRate(input: unknown, system: DhUnitSystem | null | undefined): number | null {
  const n = finite(typeof input === 'string' ? input.trim() : input);
  if (n === null) return null;
  const perFoot = isMetric(system) ? n * FT_M : n;
  return Math.round(perFoot * 10000) / 10000;
}

/**
 * Standby percentages (api#1062: dh_settings.default_standby_pct and
 * dh_rates.standby_pct). The API stores a FRACTION, 0 < x ≤ 1 (0.6 = 60%); a
 * reader types and reads a PERCENT. These convert between the two and do
 * nothing else: no price is derived from them in the browser.
 */
export function standbyPctToInput(fraction: unknown): string {
  const n = finite(fraction);
  if (n === null) return '';
  // 0.07 * 100 is 7.000000000000001 in binary floating point.
  return String(Math.round(n * 1e6) / 1e4);
}

/** A typed percent → the fraction the API stores. Blank → null (not set). NaN → undefined (refuse). */
export function standbyPctFromInput(text: string): number | null | undefined {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d+(\.\d+)?$/.test(t)) return undefined;
  const pct = Number(t);
  if (pct <= 0 || pct > 100) return undefined;
  return Math.round(pct * 1e4) / 1e6;
}

/** A stored fraction as a percent for reading: 0.6 → "60%". Absent → "—". */
export function formatStandbyPct(fraction: unknown): string {
  const input = standbyPctToInput(fraction);
  return input === '' ? '—' : `${input}%`;
}
